// Mock data loader function
// Individual APIs should import their mock data directly

// Import finance mock data
import { payables as enPayables } from './en/finance/payable';
import { receivables as enReceivables } from './en/finance/receivable';

import { payables as esPayables } from './es/finance/payable';
import { receivables as esReceivables } from './es/finance/receivable';
import { expenseReports as esExpenseReports } from './es/community/expenses';

import { payables as ptPayables } from './pt/finance/payable';
import { receivables as ptReceivables } from './pt/finance/receivable';
import { expenseReports as ptExpenseReports } from './pt/community/expenses';

// Import resources mock data
import { vendors as enVendors } from './en/resources/vendor';
import { assets as enAssets } from './en/resources/asset';
import { employees as enEmployees } from './en/employee/employee';
import { residents as enResidents } from './en/residents/resident';
import { blocks as enBlocks, units as enUnits, parkings as enParkings, amenities as enAmenities } from './en/resources/property';

import { vendors as esVendors } from './es/resources/vendor';
import { assets as esAssets } from './es/resources/asset';
import { employees as esEmployees } from './es/employee/employee';
import { residents as esResidents } from './es/residents/resident';
import { blocks as esBlocks, units as esUnits, parkings as esParkings, amenities as esAmenities } from './es/resources/property';

import { vendors as ptVendors } from './pt/resources/vendor';
import { assets as ptAssets } from './pt/resources/asset';
import { employees as ptEmployees } from './pt/employee/employee';
import { residents as ptResidents } from './pt/residents/resident';
import { blocks as ptBlocks, units as ptUnits, parkings as ptParkings, amenities as ptAmenities } from './pt/resources/property';

// Import community mock data
import { lostAndFoundItems as enLostAndFoundItems } from './en/community/lostAndFound';
import { newsItems as enNewsItems } from './en/community/news';
import { incidents as enIncidents } from './en/community/incident';
import { events as enEvents } from './en/community/calendar';
import { alerts as enAlerts } from './en/community/alert';
import { guidelines as enGuidelines } from './en/community/guideline';

import { lostAndFoundItems as esLostAndFoundItems } from './es/community/lostAndFound';
import { newsItems as esNewsItems } from './es/community/news';
import { incidents as esIncidents } from './es/community/incident';
import { calendarEvents as esEvents } from './es/community/calendar';
import { alerts as esAlerts } from './es/community/alert';
import { guidelines as esGuidelines } from './es/community/guideline';

import { lostAndFoundItems as ptLostAndFoundItems } from './pt/community/lostAndFound';
import { newsItems as ptNewsItems } from './pt/community/news';
import { incidents as ptIncidents } from './pt/community/incident';
import { events as ptEvents } from './pt/community/calendar';
import { alerts as ptAlerts } from './pt/community/alert';
import { guidelines as ptGuidelines } from './pt/community/guideline';

// Import residents mock data
import { reservations as enReservations } from './en/residents/reservations';
import { visitors as enVisitors } from './en/residents/visitor';
import { packages as enPackages } from './en/residents/package';
import { residentContacts as enResidentContacts, chatHistory as enChatHistory } from './en/residents/chat';
import { messages as enMessages } from './en/residents/message';

import { reservations as esReservations } from './es/residents/reservations';
import { visitors as esVisitors } from './es/residents/visitor';
import { packages as esPackages } from './es/residents/package';
import { residentContacts as esResidentContacts, chatHistory as esChatHistory } from './es/residents/chat';
import { messages as esMessages } from './es/residents/message';

import { reservations as ptReservations } from './pt/residents/reservations';
import { visitors as ptVisitors } from './pt/residents/visitor';
import { packages as ptPackages } from './pt/residents/package';
import { residentContacts as ptResidentContacts, chatHistory as ptChatHistory } from './pt/residents/chat';
import { messages as ptMessages } from './pt/residents/message';

// Import employee mock data
import { attendances as enAttendances } from './en/employee/attendance';
import { leaveRequests as enLeaveRequests } from './en/employee/attendance';
import { tasks as enTasks } from './en/employee/task';
import { payrolls as enPayrolls } from './en/employee/payroll';

import { attendances as esAttendances } from './es/employee/attendance';
import { leaveRequests as esLeaveRequests } from './es/employee/attendance';
import { tasks as esTasks } from './es/employee/task';
import { payrolls as esPayrolls } from './es/employee/payroll';

import { attendances as ptAttendances } from './pt/employee/attendance';
import { leaveRequests as ptLeaveRequests } from './pt/employee/attendance';
import { tasks as ptTasks } from './pt/employee/task';
import { payrolls as ptPayrolls } from './pt/employee/payroll';

// Import maintenance mock data
import { maintenanceRequests as enMaintenanceRequests } from './en/maintenance/request';

import { maintenanceRequests as esMaintenanceRequests } from './es/maintenance/request';

import { maintenanceRequests as ptMaintenanceRequests } from './pt/maintenance/request';

/**
 * Load mock data by type and language
 * @param dataType - The type of data to load
 * @param language - The language for the data (defaults to 'en')
 * @returns Promise that resolves to the mock data
 */
export async function loadMockData<T>(dataType: string, language: string = 'en'): Promise<T> {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 100));
  
  // Return language-specific mock data
  switch (dataType) {
    case 'residentContacts':
      switch (language) {
        case 'es':
          return esResidentContacts as T;
        case 'pt':
          return ptResidentContacts as T;
        case 'en':
        default:
          return enResidentContacts as T;
      }
    
    case 'payables':
      switch (language) {
        case 'es':
          return esPayables as T;
        case 'pt':
          return ptPayables as T;
        case 'en':
        default:
          return enPayables as T;
      }
    
    case 'receivables':
      switch (language) {
        case 'es':
          return esReceivables as T;
        case 'pt':
          return ptReceivables as T;
        case 'en':
        default:
          return enReceivables as T;
      }
    
    case 'expenses':
      switch (language) {
        case 'es':
          return esExpenseReports as T;
        case 'pt':
          return ptExpenseReports as T;
        case 'en':
        default:
          return esExpenseReports as T;
      }
    
    case 'vendors':
      switch (language) {
        case 'es':
          return esVendors as T;
        case 'pt':
          return ptVendors as T;
        case 'en':
        default:
          return enVendors as T;
      }
    
    case 'assets':
      switch (language) {
        case 'es':
          return esAssets as T;
        case 'pt':
          return ptAssets as T;
        case 'en':
        default:
          return enAssets as T;
      }
    
    case 'employees':
      switch (language) {
        case 'es':
          return esEmployees as T;
        case 'pt':
          return ptEmployees as T;
        case 'en':
        default:
          return enEmployees as T;
      }
    
    case 'residents':
      switch (language) {
        case 'es':
          return esResidents as T;
        case 'pt':
          return ptResidents as T;
        case 'en':
        default:
          return enResidents as T;
      }
    
    case 'blocks':
      switch (language) {
        case 'es':
          return esBlocks as T;
        case 'pt':
          return ptBlocks as T;
        case 'en':
        default:
          return enBlocks as T;
      }
    
    case 'units':
      switch (language) {
        case 'es':
          return esUnits as T;
        case 'pt':
          return ptUnits as T;
        case 'en':
        default:
          return enUnits as T;
      }
    
    case 'parkings':
      switch (language) {
        case 'es':
          return esParkings as T;
        case 'pt':
          return ptParkings as T;
        case 'en':
        default:
          return enParkings as T;
      }
    
    case 'amenities':
      switch (language) {
        case 'es':
          return esAmenities as T;
        case 'pt':
          return ptAmenities as T;
        case 'en':
        default:
          return enAmenities as T;
      }
    
    case 'lostAndFoundItems':
      switch (language) {
        case 'es':
          return esLostAndFoundItems as T;
        case 'pt':
          return ptLostAndFoundItems as T;
        case 'en':
        default:
          return enLostAndFoundItems as T;
      }
    
    case 'newsItems':
      switch (language) {
        case 'es':
          return esNewsItems as T;
        case 'pt':
          return ptNewsItems as T;
        case 'en':
        default:
          return enNewsItems as T;
      }
    
    case 'incidents':
      switch (language) {
        case 'es':
          return esIncidents as T;
        case 'pt':
          return ptIncidents as T;
        case 'en':
        default:
          return enIncidents as T;
      }
    
    case 'events':
      switch (language) {
        case 'es':
          return esEvents as T;
        case 'pt':
          return ptEvents as T;
        case 'en':
        default:
          return enEvents as T;
      }
    
    case 'alerts':
      switch (language) {
        case 'es':
          return esAlerts as T;
        case 'pt':
          return ptAlerts as T;
        case 'en':
        default:
          return enAlerts as T;
      }
    
    case 'guidelines':
      switch (language) {
        case 'es':
          return esGuidelines as T;
        case 'pt':
          return ptGuidelines as T;
        case 'en':
        default:
          return enGuidelines as T;
      }
    
    case 'reservations':
      switch (language) {
        case 'es':
          return esReservations as T;
        case 'pt':
          return ptReservations as T;
        case 'en':
        default:
          return enReservations as T;
      }
    
    case 'visitors':
      switch (language) {
        case 'es':
          return esVisitors as T;
        case 'pt':
          return ptVisitors as T;
        case 'en':
        default:
          return enVisitors as T;
      }
    
    case 'packages':
      switch (language) {
        case 'es':
          return esPackages as T;
        case 'pt':
          return ptPackages as T;
        case 'en':
        default:
          return enPackages as T;
      }
    
    case 'residentContacts':
      switch (language) {
        case 'es':
          return esResidentContacts as T;
        case 'pt':
          return ptResidentContacts as T;
        case 'en':
        default:
          return enResidentContacts as T;
      }
    
    case 'chatHistory':
      switch (language) {
        case 'es':
          return esChatHistory as T;
        case 'pt':
          return ptChatHistory as T;
        case 'en':
        default:
          return enChatHistory as T;
      }
    
    case 'messages':
      switch (language) {
        case 'es':
          return esMessages as T;
        case 'pt':
          return ptMessages as T;
        case 'en':
        default:
          return enMessages as T;
      }
    
    case 'attendances':
      switch (language) {
        case 'es':
          return esAttendances as T;
        case 'pt':
          return ptAttendances as T;
        case 'en':
        default:
          return enAttendances as T;
      }
    
    case 'leaveRequests':
      switch (language) {
        case 'es':
          return esLeaveRequests as T;
        case 'pt':
          return ptLeaveRequests as T;
        case 'en':
        default:
          return enLeaveRequests as T;
      }
    
    case 'tasks':
      switch (language) {
        case 'es':
          return esTasks as T;
        case 'pt':
          return ptTasks as T;
        case 'en':
        default:
          return enTasks as T;
      }
    
    case 'payrolls':
      switch (language) {
        case 'es':
          return esPayrolls as T;
        case 'pt':
          return ptPayrolls as T;
        case 'en':
        default:
          return enPayrolls as T;
      }
    
    case 'maintenanceRequests':
      switch (language) {
        case 'es':
          return esMaintenanceRequests as T;
        case 'pt':
          return ptMaintenanceRequests as T;
        case 'en':
        default:
          return enMaintenanceRequests as T;
      }
    
    default:
      console.warn(`loadMockData called with ${dataType} and ${language} - returning empty data`);
      return [] as T;
  }
}
